import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'dark:shadow-sm dark:shadow-black inline-flex items-center justify-center rounded-md font-sans text-sm font-semibold leading-5 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-white border dark:bg-primary text-primary-foreground hover:bg-gray-100 dark:hover:bg-primary/90',
        destructive: 'bg-red-500 hover:bg-red-400 !text-white text-destructive-foreground',
        outline:
          'border border-gray-800 bg-background hover:bg-gray-300 dark:hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-gray-100 border border-gray-800 dark:bg-gray-900 hover:dark:bg-gray-800 hover:bg-gray-200 ',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        marketing:
          'w-full sm:w-44 min-h-12 cursor-pointer rounded-lg px-6 font-semibold text-white hover:text-white transition-all duration-200 shadow-lg hover:shadow-xl border-0 relative overflow-hidden group',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
        {children}

        {variant === 'marketing' && (
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
        )}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
